import React, { useEffect, useState } from 'react';
import {
  X, ChevronDown, ChevronRight, Loader2, AlertTriangle,
  Bug, Wrench, GitPullRequest, Activity, FileText, Info,
} from 'lucide-react';
import aiAgentService from '../services/aiAgentService';
import azureDevOpsService from '../services/azureDevOpsService';

// ─── Diff ────────────────────────────────────────────────────────────────────

function computeLineDiff(oldText, newText) {
  const oldLines = (oldText || '').split('\n');
  const newLines = (newText || '').split('\n');
  const m = oldLines.length;
  const n = newLines.length;
  const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = oldLines[i - 1] === newLines[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
  const diff = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      diff.unshift({ type: 'same', line: oldLines[--i], _j: --j });
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      diff.unshift({ type: 'add', line: newLines[--j] });
    } else {
      diff.unshift({ type: 'remove', line: oldLines[--i] });
    }
  }
  return diff;
}

function DiffView({ original, updated }) {
  const diff = computeLineDiff(original, updated);
  const hasChanges = diff.some(d => d.type !== 'same');
  return (
    <div className="rounded-lg border border-gray-300 overflow-hidden text-xs font-mono">
      <div className="bg-gray-100 border-b border-gray-300 px-3 py-1 text-gray-500 flex items-center justify-between">
        <span>Diff</span>
        {!hasChanges && <span className="text-amber-600 text-xs">Aucune différence détectée</span>}
      </div>
      <div className="overflow-y-auto max-h-64 bg-white">
        {diff.map((entry, idx) => (
          <div
            key={idx}
            className={
              entry.type === 'add'
                ? 'bg-green-50 text-green-800'
                : entry.type === 'remove'
                ? 'bg-red-50 text-red-800 line-through opacity-75'
                : 'text-gray-600'
            }
          >
            <span className={`inline-block w-5 text-center select-none mr-2 font-bold ${entry.type === 'add' ? 'text-green-600' : entry.type === 'remove' ? 'text-red-500' : 'text-gray-300'}`}>
              {entry.type === 'add' ? '+' : entry.type === 'remove' ? '−' : ' '}
            </span>
            <span className="whitespace-pre-wrap break-all">{entry.line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CONFIDENCE_META = {
  high:   { label: 'Confiance élevée',  color: 'bg-green-100 text-green-700 border-green-200' },
  medium: { label: 'Confiance moyenne', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  low:    { label: 'Confiance faible',  color: 'bg-red-100 text-red-700 border-red-200' },
};

function normalizeConf(value) {
  const v = String(value || '').trim().toLowerCase();
  return v === 'high' || v === 'medium' || v === 'low' ? v : 'low';
}

function ConfBadge({ value, className = '' }) {
  const meta = CONFIDENCE_META[normalizeConf(value)];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color} ${className}`}>
      {meta.label}
    </span>
  );
}


// ─── HypothesisCard ───────────────────────────────────────────────────────────

function HypothesisCard({ hyp, idx, fixState, onBugDraft, onFixPreview, onConfirmFix, onCancelFix }) {
  const [expanded, setExpanded] = useState(true);
  const [showReasoning, setShowReasoning] = useState(false);

  const title     = typeof hyp === 'string' ? hyp : (hyp?.title || '');
  const details   = typeof hyp === 'string' ? null : (hyp?.details || null);
  const reasoning = typeof hyp === 'string' ? null : (hyp?.reasoning || hyp?.details || null);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition"
      >
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">
          {idx + 1}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-800 text-sm leading-snug pr-2">{title}</p>
          {details && !expanded && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{details}</p>
          )}
        </div>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
          {details && <p className="text-xs text-gray-600 italic">{details}</p>}

          {reasoning && (
            <div>
              <button
                onClick={() => setShowReasoning(r => !r)}
                className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
              >
                {showReasoning ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Pourquoi cette hypothèse
              </button>
              {showReasoning && (
                <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
                  <p className="text-xs text-blue-900 leading-relaxed whitespace-pre-wrap">{reasoning}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              onClick={() => onBugDraft(hyp)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-100 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-200 transition"
            >
              <Bug className="w-3 h-3" />
              Bug draft
            </button>

            {fixState?.loading ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                {fixState.stage === 'creating' ? 'Création PR…' : (fixState.statusMsg || 'Génération…')}
              </span>
            ) : fixState?.stage === 'done' ? (
              <a
                href={fixState.pr.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-green-100 text-green-700 border border-green-200 rounded-lg hover:bg-green-200 transition"
              >
                <GitPullRequest className="w-3 h-3" />
                Voir PR #{fixState.pr.id}
              </a>
            ) : fixState?.stage === 'error' ? (
              <span className="text-xs text-red-500" title={fixState.error}>Erreur correctif</span>
            ) : fixState?.stage === 'preview' ? (
              <span className="text-xs text-indigo-600 font-medium">Correctif ci-dessous ↓</span>
            ) : (
              <button
                onClick={() => onFixPreview(idx, title)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-200 transition"
              >
                <Wrench className="w-3 h-3" />
                Proposer un correctif
              </button>
            )}
          </div>

          {fixState?.stage === 'preview' && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs font-semibold text-indigo-800 truncate">{fixState.preview.filePath}</span>
                <ConfBadge value={fixState.preview.confidence} />
              </div>
              <p className="text-xs font-medium text-gray-700">PR : {fixState.preview.prTitle}</p>
              <DiffView original={fixState.preview.originalTestCase} updated={fixState.preview.updatedTestCase} />
              {fixState.preview.resourceFix && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-mono truncate">{fixState.preview.resourceFix.filePath}</p>
                  <DiffView original={fixState.preview.resourceFix.originalSnippet} updated={fixState.preview.resourceFix.updatedSnippet} />
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onConfirmFix(idx)}
                  className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Confirmer et créer la PR
                </button>
                <button
                  onClick={() => onCancelFix(idx)}
                  className="text-xs px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Collapsible log / steps block ───────────────────────────────────────────

function LogBlock({ title, badge, summary, loading, error, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{title}</span>
          {badge && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border bg-indigo-50 text-indigo-700 border-indigo-200">
              {badge}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400 flex items-center gap-1">
          {loading ? 'Chargement…' : summary}
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </span>
      </button>
      {open && <div className="border-t border-gray-200">{children}</div>}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const AiAnalysisDrawer = ({ open, onClose, testCase, context }) => {
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [analysis, setAnalysis]             = useState(null);
  const [artifactFetched, setArtifactFetched] = useState(false);
  const [fromCache, setFromCache]           = useState(false);
  const [showDraft, setShowDraft]           = useState(false);
  const [bugTitle, setBugTitle]             = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [copyStatus, setCopyStatus]         = useState('');
  const [historyMeta, setHistoryMeta]       = useState({ count: 0, lastSavedAt: null });
  const [fixStates, setFixStates]           = useState({});
  const [logPreview, setLogPreview]         = useState({ loading: false, content: null, error: null });
  const [stepsPreview, setStepsPreview]     = useState({ loading: false, content: null, error: null });
  const [userContext, setUserContext]       = useState('');
  const [activeTab, setActiveTab]           = useState('analysis');
  const [suspectsLoading, setSuspectsLoading] = useState(false);
  const [suspectsError, setSuspectsError]   = useState(null);
  const [suspectsResult, setSuspectsResult] = useState(null);
  const [suspectsFromCache, setSuspectsFromCache] = useState(false);
  const [suspectsCachedAt, setSuspectsCachedAt]   = useState(null);

  // ── Cache helpers ──────────────────────────────────────────────────────────

  const getCacheKey = () => {
    const source     = context?.source || 'unknown';
    const pipelineId = context?.pipelineId || 'na';
    const runId      = context?.runId || 'na';
    const name       = testCase?.name || testCase?.id || 'unknown';
    return `ai-analysis:${source}:${pipelineId}:${runId}:${encodeURIComponent(name)}`;
  };

  const readCachedAnalysis = () => {
    try {
      const raw = localStorage.getItem(getCacheKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      const history = Array.isArray(parsed.history) ? parsed.history : [];
      const latest = parsed.latest || (history[0] ? history[0].analysis : null);
      setHistoryMeta({ count: history.length, lastSavedAt: parsed.lastSavedAt || (history[0] ? history[0].savedAt : null) });
      return latest || null;
    } catch { return null; }
  };

  const writeCachedAnalysis = (value) => {
    try {
      const key = getCacheKey();
      const raw = localStorage.getItem(key);
      const previous = raw ? JSON.parse(raw) : {};
      const prevHistory = Array.isArray(previous?.history) ? previous.history : [];
      const nextEntry = { savedAt: new Date().toISOString(), analysis: value };
      const history = [nextEntry, ...prevHistory].slice(0, 10);
      localStorage.setItem(key, JSON.stringify({ lastSavedAt: nextEntry.savedAt, latest: value, history }));
      setHistoryMeta({ count: history.length, lastSavedAt: nextEntry.savedAt });
    } catch {}
  };

  // ── Suspects cache helpers ─────────────────────────────────────────────────

  const SUSPECTS_TTL_MS = 60 * 60 * 1000; // 1 heure

  const getSuspectsCacheKey = () => {
    const pipelineId = context?.pipelineId || 'na';
    const buildId = String(context?.buildId || '').replace(/^build-/i, '') || 'na';
    const name = testCase?.name || testCase?.id || 'unknown';
    return `ai-suspects:${pipelineId}:${buildId}:${encodeURIComponent(name)}`;
  };

  const readCachedSuspects = () => {
    try {
      const raw = localStorage.getItem(getSuspectsCacheKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.result || !parsed?.savedAt) return null;
      if (Date.now() - new Date(parsed.savedAt).getTime() > SUSPECTS_TTL_MS) {
        localStorage.removeItem(getSuspectsCacheKey());
        return null;
      }
      return parsed;
    } catch { return null; }
  };

  const writeCachedSuspects = (result) => {
    try {
      localStorage.setItem(getSuspectsCacheKey(), JSON.stringify({ result, savedAt: new Date().toISOString() }));
    } catch {}
  };

  // ── Analysis ───────────────────────────────────────────────────────────────

  const runAnalysis = async (force = false, testSteps = null, extraContext = '') => {
    if (!testCase) return;
    if (!force) {
      const cached = readCachedAnalysis();
      if (cached) { setAnalysis(cached); setFromCache(true); setError(null); return; }
    }
    try {
      setLoading(true); setError(null); setFromCache(false);
      const pat = localStorage.getItem('AZDO_PAT') || undefined;
      const response = await aiAgentService.analyzeTestFailure({
        testCase: { ...testCase, ...(testSteps ? { testSteps } : {}) },
        context: context || {},
        ...(extraContext ? { userContext: extraContext } : {}),
        ...(pat ? { pat } : {}),
      });
      const result = response.analysis || null;
      setAnalysis(result);
      setArtifactFetched(Boolean(response.artifactFetched));
      if (result) writeCachedAnalysis(result);
    } catch (err) {
      setError(err.message || 'Erreur analyse IA');
    } finally {
      setLoading(false);
    }
  };

  const loadLogPreview = async () => {
    if (!testCase?.name) return;
    if (context?.source === 'readyapi' && testCase.scenario) {
      setLogPreview({ loading: false, content: testCase.scenario, error: null });
      return;
    }
    setLogPreview({ loading: true, content: null, error: null });
    try {
      const pat = localStorage.getItem('AZDO_PAT') || undefined;
      const content = await aiAgentService.fetchArtifactLog({
        runId: context?.runId, buildId: context?.buildId,
        pipelineId: context?.pipelineId, testName: testCase.name,
        source: context?.source, testSuiteName: context?.testSuiteName,
        ...(pat ? { pat } : {}),
      });
      setLogPreview({ loading: false, content, error: null });
    } catch (err) {
      setLogPreview({ loading: false, content: null, error: err.message || 'Log indisponible' });
    }
  };

  const loadStepsPreview = async () => {
    if (!testCase?.name) return null;
    setStepsPreview({ loading: true, content: null, error: null });
    try {
      const pat = localStorage.getItem('AZDO_PAT') || undefined;
      const data = await aiAgentService.fetchTestCaseSteps({ testName: testCase.name, ...(pat ? { pat } : {}) });
      const content = data.steps || null;
      setStepsPreview({ loading: false, content, error: null });
      return content;
    } catch (err) {
      setStepsPreview({ loading: false, content: null, error: err.message || 'Steps indisponibles' });
      return null;
    }
  };

  const runSuspectsAnalysis = async (force = false) => {
    if (!force) {
      const cached = readCachedSuspects();
      if (cached) {
        setSuspectsResult(cached.result);
        setSuspectsFromCache(true);
        setSuspectsCachedAt(cached.savedAt);
        setSuspectsError(null);
        return;
      }
    }
    const pat = localStorage.getItem('AZDO_PAT');
    if (!pat) { setSuspectsError('PAT Azure DevOps introuvable (clé: AZDO_PAT). Connectez-vous d\'abord.'); return; }
    const failingBuildId = String(context?.buildId || '').replace(/^build-/i, '') || null;
    if (!failingBuildId) { setSuspectsError('buildId requis dans le contexte.'); return; }
    try {
      setSuspectsLoading(true); setSuspectsError(null); setSuspectsFromCache(false); setSuspectsCachedAt(null);
      let lastGreenBuildId = null;
      if (context?.pipelineId && testCase?.name) {
        try {
          const searchName = testCase.automatedTestName || testCase.name;
          const history = await azureDevOpsService.getAutomatedTestHistory(searchName, context.pipelineId);
          const lastGreen = history.find(h => h.status === 'passed' && String(h.buildId) !== String(failingBuildId));
          lastGreenBuildId = lastGreen?.buildId ?? null;
        } catch {}
      }
      const result = await aiAgentService.analyzeSuspectChanges({
        failingBuildId, lastGreenBuildId, pipelineId: context?.pipelineId, testName: testCase?.name, pat,
      });
      setSuspectsResult(result);
      writeCachedSuspects(result);
      setSuspectsCachedAt(new Date().toISOString());
    } catch (err) {
      setSuspectsError(err.message || 'Erreur analyse changements suspects');
    } finally {
      setSuspectsLoading(false);
    }
  };

  const runFixPreview = async (hypIdx, hypothesis) => {
    setFixStates(prev => ({ ...prev, [hypIdx]: { stage: 'previewing', loading: true, statusMsg: 'Initialisation…' } }));
    try {
      const pat = localStorage.getItem('AZDO_PAT') || undefined;
      const result = await aiAgentService.previewFix(
        { testName: testCase?.name, hypothesis, error: testCase?.error, stackTrace: testCase?.stackTrace, ...(pat ? { pat } : {}) },
        (msg) => setFixStates(prev => ({ ...prev, [hypIdx]: { ...prev[hypIdx], statusMsg: msg } })),
      );
      setFixStates(prev => ({ ...prev, [hypIdx]: { stage: 'preview', loading: false, preview: result, hypothesis } }));
    } catch (err) {
      setFixStates(prev => ({ ...prev, [hypIdx]: { stage: 'error', loading: false, error: err.message || 'Erreur' } }));
    }
  };

  const confirmFixPR = async (hypIdx) => {
    const state = fixStates[hypIdx];
    if (!state?.preview) return;
    setFixStates(prev => ({ ...prev, [hypIdx]: { ...prev[hypIdx], stage: 'creating', loading: true } }));
    try {
      const pat = localStorage.getItem('AZDO_PAT') || undefined;
      const result = await aiAgentService.createFixPR({
        testName: testCase?.name, hypothesis: state.hypothesis, preview: state.preview, ...(pat ? { pat } : {}),
      });
      setFixStates(prev => ({ ...prev, [hypIdx]: { stage: 'done', loading: false, pr: result.pr } }));
    } catch (err) {
      setFixStates(prev => ({ ...prev, [hypIdx]: { stage: 'error', loading: false, error: err.message || 'Erreur création PR' } }));
    }
  };

  const buildBugDraft = (singleHypothesis = null) => {
    const getHypTitle = (h) => typeof h === 'string' ? h : (h?.title || '');
    const hypotheses = singleHypothesis
      ? `- ${getHypTitle(singleHypothesis)}`
      : (analysis?.hypotheses || []).map((h) => `- ${getHypTitle(h)}`).join('\n') || '- (aucune)';
    const nextActions  = (analysis?.nextActions || []).map(a => `- ${a}`).join('\n') || '- (aucune)';
    const missingData  = (analysis?.missingData  || []).map(m => `- ${m}`).join('\n') || '- (aucune)';
    const title = `[AI][${String(context?.source || 'tests').toUpperCase()}] Echec test ${testCase?.name || '(sans nom)'} - run ${context?.runId || 'N/A'}`;
    const description = [
      '## Contexte',
      `- Source: ${context?.source || 'N/A'}`,
      `- Pipeline: ${context?.pipelineId || 'N/A'}`,
      `- Build: ${context?.buildId || 'N/A'}`,
      `- Run: ${context?.runId || 'N/A'}`,
      `- Test suite: ${context?.testSuiteName || 'N/A'}`,
      `- Test case: ${testCase?.name || 'N/A'}`,
      `- Statut: ${testCase?.status || 'N/A'}`,
      '', '## Resume IA', analysis?.summary || '-',
      '', '## Confiance', `- ${normalizeConf(analysis?.confidence)}`,
      '', '## Hypotheses', hypotheses,
      '', '## Next Actions', nextActions,
      '', '## Donnees manquantes', missingData,
      '', '## Erreur brute', testCase?.error || '(non fournie)',
      '', '## Stack trace', testCase?.stackTrace || '(non fournie)',
    ].join('\n');
    setBugTitle(title);
    setBugDescription(description);
    setShowDraft(true);
    setCopyStatus('');
  };

  useEffect(() => {
    if (!open || !testCase) return;
    setActiveTab('analysis');
    setShowDraft(false);
    setBugTitle('');
    setBugDescription('');
    setCopyStatus('');
    setHistoryMeta({ count: 0, lastSavedAt: null });
    setArtifactFetched(false);
    setFixStates({});
    setLogPreview({ loading: false, content: null, error: null });
    setStepsPreview({ loading: false, content: null, error: null });
    setUserContext('');
    setSuspectsResult(null);
    setSuspectsError(null);
    setSuspectsFromCache(false);
    setSuspectsCachedAt(null);

    const isRF = context?.source !== 'readyapi' && context?.source !== 'data-preparation-readyapi';
    const init = async () => {
      loadLogPreview();
      const fetchedSteps = isRF && testCase?.name ? await loadStepsPreview() : null;
      runAnalysis(false, fetchedSteps);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, testCase?.id, testCase?.name]);

  useEffect(() => {
    if (activeTab !== 'suspects' || suspectsResult) return;
    const cached = readCachedSuspects();
    if (cached) {
      setSuspectsResult(cached.result);
      setSuspectsFromCache(true);
      setSuspectsCachedAt(cached.savedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Overlay */}
      {open && <div className="fixed inset-0 bg-black/20 z-[70]" onClick={onClose} />}

      {/* Panel (slide-in) */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-[80] flex flex-col transform transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="font-bold text-base">Analyse IA — Test unitaire</div>
            <div className="text-xs text-indigo-200 mt-0.5 truncate">
              {testCase?.name || 'Test sélectionné'}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-5 bg-white flex-shrink-0">
          {[
            { key: 'analysis', label: "Analyse d'échec" },
            { key: 'suspects', label: 'Changements suspects' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`text-sm py-3 pr-4 font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* ── Onglet Analyse d'échec ── */}
          {activeTab === 'analysis' && (
            <>
              {/* Log artefact */}
              {context?.runId && testCase?.name && (
                <LogBlock
                  title="Log artefact"
                  badge={artifactFetched ? "inclus dans l'analyse" : undefined}
                  loading={logPreview.loading}
                  summary={
                    logPreview.content
                      ? `${logPreview.content.length.toLocaleString()} chars`
                      : logPreview.error
                      ? 'Indisponible'
                      : ''
                  }
                >
                  {logPreview.loading && <div className="px-4 py-4 text-xs text-gray-500">Chargement…</div>}
                  {!logPreview.loading && logPreview.error && (
                    <div className="px-4 py-3 text-xs text-red-600">{logPreview.error}</div>
                  )}
                  {!logPreview.loading && !logPreview.error && logPreview.content && (
                    <pre className="text-xs font-mono p-3 max-h-72 overflow-y-auto bg-gray-900 text-gray-100 whitespace-pre-wrap break-all">
                      {logPreview.content}
                    </pre>
                  )}
                  {!logPreview.loading && !logPreview.error && !logPreview.content && (
                    <div className="px-4 py-3 text-xs text-gray-500">Aucun log disponible.</div>
                  )}
                </LogBlock>
              )}

              {/* Étapes du test (RF only) */}
              {context?.source !== 'readyapi' && context?.source !== 'data-preparation-readyapi' && testCase?.name && (
                <LogBlock
                  title="Étapes du test"
                  badge={stepsPreview.content ? "incluses dans l'analyse" : undefined}
                  loading={stepsPreview.loading}
                  summary={
                    stepsPreview.content
                      ? `${stepsPreview.content.split('\n').length} lignes`
                      : stepsPreview.error
                      ? 'Introuvable'
                      : ''
                  }
                >
                  {stepsPreview.loading && <div className="px-4 py-4 text-xs text-gray-500">Chargement…</div>}
                  {!stepsPreview.loading && stepsPreview.error && (
                    <div className="px-4 py-3 text-xs text-red-600">{stepsPreview.error}</div>
                  )}
                  {!stepsPreview.loading && !stepsPreview.error && stepsPreview.content && (
                    <pre className="text-xs font-mono p-3 max-h-48 overflow-y-auto bg-white text-gray-800 whitespace-pre-wrap">
                      {stepsPreview.content}
                    </pre>
                  )}
                  {!stepsPreview.loading && !stepsPreview.error && !stepsPreview.content && (
                    <div className="px-4 py-3 text-xs text-gray-500">Aucune étape trouvée dans Azure Test Plans.</div>
                  )}
                </LogBlock>
              )}

              {/* Loading */}
              {loading && (
                <div className="flex flex-col items-center justify-center py-10 space-y-3 text-center">
                  <Loader2 className="w-9 h-9 text-indigo-500 animate-spin" />
                  <p className="font-semibold text-gray-700">Analyse en cours…</p>
                  <p className="text-sm text-gray-400">Identification des causes et hypothèses</p>
                </div>
              )}

              {/* Erreur */}
              {!loading && error && (
                <div className="flex flex-col items-center justify-center py-8 space-y-3 text-center">
                  <AlertTriangle className="w-9 h-9 text-red-400" />
                  <p className="font-semibold text-gray-700">Erreur lors de l'analyse</p>
                  <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 border border-red-200">{error}</p>
                  <button
                    onClick={() => runAnalysis(true, stepsPreview.content, userContext)}
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition"
                  >
                    Réessayer
                  </button>
                </div>
              )}

              {/* Pas d'analyse */}
              {!loading && !error && !analysis && (
                <div className="flex flex-col items-center justify-center py-10 space-y-4 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center">
                    <Activity className="w-7 h-7 text-indigo-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Prêt à analyser</p>
                    <p className="text-sm text-gray-500 mt-1">L'IA va identifier les hypothèses et proposer des actions.</p>
                  </div>
                  <button
                    onClick={() => runAnalysis(true, stepsPreview.content, userContext)}
                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition shadow"
                  >
                    Lancer l'analyse
                  </button>
                </div>
              )}

              {/* Résultats */}
              {!loading && !error && analysis && (
                <>
                  {/* Résumé */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-sm font-semibold text-indigo-800">Résumé</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <ConfBadge value={analysis.confidence} />
                        {artifactFetched && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-700 border-blue-200">
                            log inclus
                          </span>
                        )}
                        {stepsPreview.content && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-green-100 text-green-700 border-green-200">
                            étapes incluses
                          </span>
                        )}
                        {fromCache && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200">
                            cache
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-indigo-700 leading-relaxed whitespace-pre-wrap">{analysis.summary || '-'}</p>
                    {historyMeta.lastSavedAt && (
                      <p className="text-xs text-indigo-400">
                        Dernière analyse : {new Date(historyMeta.lastSavedAt).toLocaleString('fr-FR')}
                        {historyMeta.count > 1 && ` · ${historyMeta.count} dans l'historique`}
                      </p>
                    )}
                  </div>

                  {/* Hypothèses */}
                  {(analysis.hypotheses || []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {analysis.hypotheses.length} hypothèse{analysis.hypotheses.length > 1 ? 's' : ''} identifiée{analysis.hypotheses.length > 1 ? 's' : ''}
                      </p>
                      {analysis.hypotheses.map((hyp, idx) => (
                        <HypothesisCard
                          key={`hyp-${idx}`}
                          hyp={hyp}
                          idx={idx}
                          fixState={fixStates[idx]}
                          onBugDraft={buildBugDraft}
                          onFixPreview={runFixPreview}
                          onConfirmFix={confirmFixPR}
                          onCancelFix={(i) => setFixStates(prev => ({ ...prev, [i]: undefined }))}
                        />
                      ))}
                    </div>
                  )}

                  {/* Next Actions */}
                  {(analysis.nextActions || []).length > 0 && (
                    <div className="border border-gray-200 rounded-xl px-4 py-3 space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions recommandées</p>
                      <ul className="space-y-1.5">
                        {analysis.nextActions.map((item, idx) => (
                          <li key={`next-${idx}`} className="flex items-start gap-2 text-sm text-gray-700">
                            <span className="text-indigo-400 mt-0.5">→</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Données manquantes */}
                  {(analysis.missingData || []).length > 0 && (
                    <div className="border border-amber-200 rounded-xl px-4 py-3 space-y-2 bg-amber-50">
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                        <Info className="w-3.5 h-3.5" /> Données manquantes
                      </p>
                      <ul className="space-y-1">
                        {analysis.missingData.map((item, idx) => (
                          <li key={`missing-${idx}`} className="text-sm text-amber-800">• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Contexte utilisateur + réanalyse */}
                  <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Précisions supplémentaires
                      <span className="normal-case font-normal text-gray-400 ml-1">(optionnel)</span>
                    </label>
                    <textarea
                      value={userContext}
                      onChange={e => setUserContext(e.target.value)}
                      placeholder="Ex : ce test fonctionne en local, l'erreur n'apparaît qu'en CI…"
                      rows={3}
                      className="w-full text-xs rounded-lg border border-gray-300 px-3 py-2 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 resize-y bg-white"
                    />
                    <button
                      onClick={() => runAnalysis(true, stepsPreview.content, userContext)}
                      className="text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                      Réanalyser
                    </button>
                  </div>

                  {/* Draft bug */}
                  {showDraft && (
                    <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white shadow-sm">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-800">Draft bug pré-rempli</h4>
                        <button
                          onClick={() => setShowDraft(false)}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          Masquer
                        </button>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">Titre</label>
                        <input
                          value={bugTitle}
                          onChange={e => setBugTitle(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">Description</label>
                        <textarea
                          value={bugDescription}
                          onChange={e => setBugDescription(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[180px] font-mono focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-y"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(`${bugTitle}\n\n${bugDescription}`);
                              setCopyStatus('Copié !');
                            } catch { setCopyStatus('Copie impossible'); }
                          }}
                          className="text-sm px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-black transition"
                        >
                          Copier le draft
                        </button>
                        {copyStatus && <span className="text-xs text-gray-600">{copyStatus}</span>}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Onglet Changements suspects ── */}
          {activeTab === 'suspects' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>Comment ça marche ?</strong> L'IA analyse les PRs mergées entre le dernier build{' '}
                  <span className="font-medium text-green-700">vert</span> et le build{' '}
                  <span className="font-medium text-red-600">en échec</span>, et les corrèle avec le test KO.
                </p>
              </div>

              {!suspectsResult && !suspectsLoading && !suspectsError && (
                <div className="flex flex-col items-center py-8 space-y-4 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center">
                    <FileText className="w-7 h-7 text-indigo-400" />
                  </div>
                  <button
                    onClick={runSuspectsAnalysis}
                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition shadow"
                  >
                    Analyser les changements suspects
                  </button>
                </div>
              )}

              {suspectsLoading && (
                <div className="flex flex-col items-center py-10 space-y-3 text-center">
                  <Loader2 className="w-9 h-9 text-indigo-500 animate-spin" />
                  <p className="font-semibold text-gray-700">Récupération et analyse en cours…</p>
                </div>
              )}

              {!suspectsLoading && suspectsError && (
                <div className="flex flex-col items-center py-8 space-y-3 text-center">
                  <AlertTriangle className="w-9 h-9 text-red-400" />
                  <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 border border-red-200">{suspectsError}</p>
                  <button
                    onClick={runSuspectsAnalysis}
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition"
                  >
                    Réessayer
                  </button>
                </div>
              )}

              {!suspectsLoading && !suspectsError && suspectsResult && (
                <>
                  {/* Fenêtre de comparaison */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 space-y-1 text-xs text-gray-600">
                    {suspectsResult.window?.lastGreenBuild
                      ? <p>🟢 Last Passed : Build <strong>#{suspectsResult.window.lastGreenBuild.buildNumber}</strong></p>
                      : <p>🟢 Last Passed : introuvable</p>}
                    <p>🔴 Build en échec : <strong>#{suspectsResult.window?.failingBuild?.buildNumber}</strong></p>
                    {suspectsResult.devContext && (
                      <>
                        <p>📦 Repo : <strong>{suspectsResult.devContext.repo}</strong> · branche <strong>{suspectsResult.devContext.branch}</strong></p>
                        <p>{suspectsResult.devContext.prCount} PR(s) mergée(s) dans la fenêtre</p>
                      </>
                    )}
                  </div>

                  {/* Résumé */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-sm font-semibold text-indigo-800">Résumé</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {suspectsResult.confidence && <ConfBadge value={suspectsResult.confidence} />}
                        {suspectsFromCache && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200">
                            cache
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-indigo-700 leading-relaxed whitespace-pre-wrap">{suspectsResult.summary || '-'}</p>
                    {suspectsCachedAt && (
                      <p className="text-xs text-indigo-400">
                        Dernière analyse : {new Date(suspectsCachedAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>

                  {/* Suspects */}
                  {(suspectsResult.suspects || []).length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Changements suspects classés</p>
                      {suspectsResult.suspects.map((s, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-xl p-3 bg-white shadow-sm space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-white bg-gray-600 rounded-full px-2 py-0.5 flex-shrink-0">
                              #{s.rank ?? idx + 1}
                            </span>
                            <span className="text-sm font-medium text-gray-800 truncate">{s.message || s.changeId || '(sans message)'}</span>
                          </div>
                          {s.rationale && <p className="text-xs text-gray-600 ml-7">{s.rationale}</p>}
                          {s.url && (
                            <a href={s.url} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:underline ml-7 inline-block">
                              Voir la PR →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">Aucun changement suspect identifié.</p>
                  )}

                  <button
                    onClick={() => runSuspectsAnalysis(true)}
                    className="w-full py-2 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 transition"
                  >
                    Relancer l'analyse
                  </button>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default AiAnalysisDrawer;
